import {
  LayoutDashboard, Building2, KanbanSquare, Rocket, Sparkles, Activity,
  CheckSquare, CalendarDays, Upload, Users, BarChart3, Settings,
  Ban, Wand2, Trophy, PhoneCall, UserRound, Mail, Zap,
  Gauge, Radar, Handshake, MessagesSquare, Bot, ShieldCheck, Bug, Package, PhoneOutgoing,
} from "lucide-react";

/**
 * Navigation du CRM, groupée par étape du métier plutôt qu'en liste plate :
 * 20 entrées au même niveau devenaient illisibles et obligeaient à scanner
 * toute la barre pour trouver un écran.
 *
 * `roles` (sur un groupe ou une entrée) = visibilité. Un groupe dont toutes
 * les entrées sont filtrées disparaît entièrement (voir visibleGroups).
 * `end` = correspondance exacte de route (react-router), nécessaire pour "/"
 * qui matcherait sinon toutes les pages.
 * `children` = sous-entrées d'un écran (un seul niveau) : rendues en retrait
 * sous leur parent dans la barre latérale, filtrées par rôle comme le reste,
 * jamais reprises dans la barre d'onglets mobile.
 */
export const NAV_GROUPS = [
  {
    id: "pilotage",
    label: "Pilotage",
    icon: Gauge,
    items: [
      { to: "/", label: "Tableau de bord", icon: LayoutDashboard, end: true },
      { to: "/rapports", label: "Rapports", icon: BarChart3, roles: ["admin", "manager"] },
      { to: "/comparaison", label: "Comparaison", icon: Trophy, roles: ["admin", "manager"] },
    ],
  },
  {
    id: "prospection",
    label: "Prospection",
    icon: Radar,
    items: [
      { to: "/entreprises", label: "Entreprises", icon: Building2 },
      { to: "/contacts", label: "Contacts", icon: UserRound },
      { to: "/solutions", label: "Solutions", icon: Package },
      // Campagnes : un parent, deux sous-entrées. L'extraction est réservée à
      // l'admin ; les campagnes d'appel sont ouvertes à tous (un commercial y
      // trouve celles où il est affecté). Pour un rôle qui ne voit pas le
      // parent, `visibleGroups` aplatit : une seule sous-entrée permise devient
      // une entrée simple sous son `flatLabel` (« Campagnes d'appel »), sinon
      // le lien du parent mènerait à une page refusée.
      { to: "/campagnes", label: "Campagnes", icon: Rocket, roles: ["admin"], end: true,
        children: [
          { to: "/campagnes", label: "Extraction", icon: Rocket, roles: ["admin"], end: true,
            flatLabel: "Campagnes d'extraction" },
          { to: "/campagnes/appel", label: "Appel", icon: PhoneOutgoing, flatLabel: "Campagnes d'appel" },
        ] },
      { to: "/enrichissement", label: "Enrichissement", icon: Sparkles },
      { to: "/import", label: "Import / Export", icon: Upload, roles: ["admin", "manager"] },
    ],
  },
  {
    id: "suivi",
    label: "Suivi commercial",
    icon: Handshake,
    items: [
      { to: "/pipeline", label: "Pipeline", icon: KanbanSquare },
      { to: "/activites", label: "Activités", icon: Activity },
      { to: "/taches", label: "Tâches", icon: CheckSquare },
      { to: "/calendrier", label: "Calendrier", icon: CalendarDays },
    ],
  },
  {
    id: "communication",
    label: "Communication",
    icon: MessagesSquare,
    items: [
      { to: "/telephonie", label: "Téléphonie", icon: PhoneCall },
      { to: "/templates-emails", label: "Templates emails", icon: Mail, roles: ["admin", "manager"] },
      { to: "/oppositions", label: "Oppositions", icon: Ban },
    ],
  },
  {
    id: "automatisation",
    label: "Automatisation & IA",
    icon: Bot,
    roles: ["admin", "manager"],
    items: [
      { to: "/automatisations", label: "Automatisations", icon: Zap, roles: ["admin", "manager"] },
      { to: "/modeles-ia", label: "Modèles IA", icon: Wand2, roles: ["admin", "manager"] },
    ],
  },
  {
    id: "administration",
    label: "Administration",
    icon: ShieldCheck,
    roles: ["admin", "manager"],
    items: [
      { to: "/equipe", label: "Équipe", icon: Users, roles: ["admin", "manager"] },
      // `end` : sans lui, Paramètres resterait aussi marqué actif sur sa
      // sous-page /parametres/debug — deux entrées surlignées à la fois.
      { to: "/parametres", label: "Paramètres", icon: Settings, roles: ["admin", "manager"], end: true,
        children: [
          // Organisation : appartenances, plan et consommation (plan V4 §21, §41).
          // Distinct d'« Équipe », qui gère les COMPTES et non les appartenances.
          { to: "/parametres/organisation", label: "Organisation", icon: Building2, roles: ["admin", "manager"] },
          { to: "/parametres/debug", label: "Mode Debug", icon: Bug, roles: ["admin"] },
        ] },
    ],
  },
];

const allowed = (entry, role) => !entry.roles || entry.roles.includes(role);

/** Groupes visibles pour un rôle, vidés de leurs entrées interdites. */
export function visibleGroups(role) {
  return NAV_GROUPS
    .filter((g) => allowed(g, role))
    .map((g) => ({
      ...g,
      items: g.items
        .map((i) => {
          if (!i.children) return allowed(i, role) ? i : null;
          const children = i.children.filter((c) => allowed(c, role));
          if (allowed(i, role)) return { ...i, children };
          if (children.length === 0) return null;
          // Parent interdit, une seule sous-entrée permise : une entrée simple,
          // sous son nom complet — un sous-menu d'un seul élément n'apporte
          // rien, et le lien du parent mènerait à une page refusée.
          if (children.length === 1) {
            const { flatLabel, ...child } = children[0];
            return { ...child, label: flatLabel || child.label, icon: child.icon || i.icon };
          }
          // Plusieurs : le parent prend l'adresse de la première.
          return { ...i, roles: undefined, to: children[0].to, end: children[0].end, children };
        })
        .filter(Boolean),
    }))
    .filter((g) => g.items.length > 0);
}

/** Identifiant du groupe contenant la route active (pour l'ouvrir d'office). */
export function groupIdForPath(pathname, role) {
  const groups = visibleGroups(role);
  // Le chemin le plus long l'emporte : /entreprises/42 doit matcher /entreprises,
  // pas "/" (qui préfixe tout).
  let best = null;
  for (const g of groups) {
    for (const item of g.items.flatMap((i) => [i, ...(i.children || [])])) {
      const match = item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(item.to + "/");
      if (match && (!best || item.to.length > best.length)) best = { length: item.to.length, id: g.id, to: item.to };
    }
  }
  return best?.id || null;
}

/** Entrée parente dont une sous-entrée porte la route active.
 *
 *  Les sous-menus sont repliés par défaut dans la barre latérale : celui de la
 *  page ouverte doit l'être aussi peu que le groupe qui la contient, sinon on
 *  ne voit plus où l'on se trouve après un rechargement.
 */
export function parentPathForPath(pathname, role) {
  for (const group of visibleGroups(role)) {
    for (const item of group.items) {
      for (const child of item.children || []) {
        const match = child.end
          ? pathname === child.to
          : pathname === child.to || pathname.startsWith(child.to + "/");
        if (match) return item.to;
      }
    }
  }
  return null;
}

/** Barre d'onglets du bas (mobile, < 760 px) : quatre écrans de tête, le
 *  cinquième onglet ouvrant le tiroir. On part des écrans les plus utilisés
 *  en prospection et on comble avec les suivants quand le rôle n'y a pas
 *  accès (un commercial ne voit pas les campagnes). */
const BOTTOM_PREFERRED = ["/", "/entreprises", "/campagnes", "/taches"];
const BOTTOM_FALLBACK = ["/contacts", "/pipeline", "/activites", "/telephonie"];
export function bottomNavItems(role) {
  const byPath = new Map(visibleGroups(role).flatMap((g) => g.items).map((i) => [i.to, i]));
  const picked = BOTTOM_PREFERRED.map((p) => byPath.get(p)).filter(Boolean);
  for (const p of BOTTOM_FALLBACK) {
    if (picked.length >= 4) break;
    const item = byPath.get(p);
    if (item && !picked.includes(item)) picked.push(item);
  }
  return picked.slice(0, 4).map((i) => (i.to === "/" ? { ...i, label: "Accueil" } : i));
}

/** data-testid stable, dérivé du libellé (accents/espaces retirés). */
export const navTestId = (label) =>
  `nav-${label.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`;
